<script setup>
import { reactive, watch } from 'vue'

const props = defineProps({
    node: Object,
})

const emit = defineEmits(['update:config'])

// Goals are evaluated against the contact's own attributes, so only offer
// fields a Lead actually has.
const fieldOptions = [
    { value: 'email', label: 'Email' },
    { value: 'name', label: 'Name' },
    { value: 'phone', label: 'Phone' },
    { value: 'title', label: 'Title' },
    { value: 'company', label: 'Company' },
    { value: 'source', label: 'Source' },
]

const operatorOptions = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Not Contains' },
    { value: 'is_empty', label: 'Is Empty' },
    { value: 'is_not_empty', label: 'Is Not Empty' },
]

function makeRule() {
    return { field: 'email', operator: 'equals', value: '' }
}

const form = reactive({
    label: props.node?.data?.config?.label ?? 'Goal',
    operator: props.node?.data?.config?.operator ?? 'AND',
    rules: (props.node?.data?.config?.rules ?? []).length > 0
        ? [...props.node.data.config.rules]
        : [makeRule()],
})

watch(() => props.node, (node) => {
    if (!node) return
    form.label = node.data?.config?.label ?? 'Goal'
    form.operator = node.data?.config?.operator ?? 'AND'
    form.rules = (node.data?.config?.rules ?? []).length > 0 ? [...node.data.config.rules] : [makeRule()]
})

function addRule() {
    form.rules.push(makeRule())
    emitUpdate()
}

function removeRule(index) {
    form.rules.splice(index, 1)
    emitUpdate()
}

function emitUpdate() {
    emit('update:config', { label: form.label, operator: form.operator, rules: [...form.rules] })
}
</script>

<template>
    <div class="config-panel__section">
        <label class="config-panel__label">Goal Label</label>
        <input v-model="form.label" class="config-panel__input" type="text" placeholder="e.g. Purchase Completed" @input="emitUpdate" />

        <label class="config-panel__label">Match Conditions</label>
        <div class="condition-builder__operator-row">
            <button
                class="condition-builder__operator-btn"
                :class="{ active: form.operator === 'AND' }"
                @click="form.operator = 'AND'; emitUpdate()"
                type="button"
            >ALL (AND)</button>
            <button
                class="condition-builder__operator-btn"
                :class="{ active: form.operator === 'OR' }"
                @click="form.operator = 'OR'; emitUpdate()"
                type="button"
            >ANY (OR)</button>
        </div>

        <div class="condition-builder__rules">
            <div v-for="(rule, index) in form.rules" :key="index" class="condition-builder__rule">
                <select v-model="rule.field" class="config-panel__select config-panel__select--sm" @change="emitUpdate">
                    <option v-for="f in fieldOptions" :key="f.value" :value="f.value">{{ f.label }}</option>
                </select>
                <select v-model="rule.operator" class="config-panel__select config-panel__select--sm" @change="emitUpdate">
                    <option v-for="o in operatorOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
                </select>
                <input
                    v-if="!['is_empty', 'is_not_empty'].includes(rule.operator)"
                    v-model="rule.value"
                    class="config-panel__input config-panel__input--sm"
                    type="text"
                    placeholder="Value"
                    @input="emitUpdate"
                />
                <button class="condition-builder__remove-btn" @click="removeRule(index)" type="button">&times;</button>
            </div>
        </div>

        <button class="condition-builder__add-btn" @click="addRule" type="button">+ Add Rule</button>
    </div>
</template>

<style scoped>
.condition-builder__operator-row {
    display: flex;
    gap: 6px;
    margin-bottom: 10px;
}
.condition-builder__operator-btn {
    flex: 1;
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid #d1d5db;
    background: white;
    font-size: 12px;
    cursor: pointer;
}
.condition-builder__operator-btn.active {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
}
.condition-builder__rules {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 8px;
}
.condition-builder__rule {
    display: flex;
    gap: 4px;
    align-items: center;
    flex-wrap: wrap;
}
.condition-builder__remove-btn {
    background: none;
    border: none;
    color: #ef4444;
    cursor: pointer;
    font-size: 16px;
    line-height: 1;
    padding: 0 2px;
}
.condition-builder__add-btn {
    background: none;
    border: 1px dashed #9ca3af;
    color: #6b7280;
    border-radius: 4px;
    padding: 4px 10px;
    font-size: 12px;
    cursor: pointer;
    width: 100%;
}
.condition-builder__add-btn:hover {
    border-color: #3b82f6;
    color: #3b82f6;
}
</style>
